import { Table, Button, Card, Row, Col, Select, DatePicker, Tag, Tooltip } from "antd";
import { useCallback, useEffect, useState, useMemo } from "react";
import { Api_Url } from "../../../config/config";
import axios from "axios";
import { CustomerFeedbackModalAdd } from "./CustomerFeedbackModalAdd";
import { ErrorToast, SuccessToast } from "../../ToastComponent";

const { Option } = Select;
const { RangePicker } = DatePicker;


// Cheque Handover Mode Options (to populate filter)
const HANDOVER_MODES = [
    "Customer Visited Branch",
    "Process through NEFT/RTGS",
    "Cheque handover to Lawyer",
];

// Helper to format Date objects consistently 
const formatDate = (date, format = "DD-MM-YYYY") => {
    if (!date) return 'N/A';
    const d = new Date(date);
    if (isNaN(d.getTime())) return 'N/A';

    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    const period = hours >= 12 ? 'PM' : 'AM';
    const hour12 = hours % 12 || 12;

    switch (format) {
        case "DD-MM-YYYY hh:mm A":
            return `${day}-${month}-${year} ${hour12}:${minutes} ${period}`;
        case "DD-MM-YYYY":
        default:
            return `${day}-${month}-${year}`;
    }
};

const parseDate = (dateString) => {
    if (!dateString) return null;
    const parsed = new Date(dateString);
    return isNaN(parsed.getTime()) ? null : parsed;
};


export const CustomerFeedback = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [branches, setBranches] = useState([]); 

  const [searchFilters, setSearchFilters] = useState({
    branch: "",
    chequeHandoverMode: null, 
    dateRange: null, 
  });

  const fetchBranches = async () => {
    try {
        const response = await axios.get(
            `${Api_Url}api/v1/Branch?PageNumber=1&PageSize=1000`,
            {
                headers: {
                    Authorization: `Bearer ${
                        JSON.parse(sessionStorage.getItem("user"))?.accessToken
                    }`,
                },
            }
        );
        const branchNames = response?.data?.data?.map(b => b.name) || [];
        setBranches([...new Set(branchNames)]);
    } catch (error) {
        console.error("Failed to load branches for filter:", error);
    }
  };


  const fetchCustomerFeedbackData = async () => {
    try {
      setLoading(true);
      const userId = JSON.parse(sessionStorage.getItem("user"))?.id;
      const response = await axios.get(
        `${Api_Url}api/v1/CustomerFeedback?UserId=${userId}&PageNumber=1&PageSize=500`,
        {
          headers: {
            Authorization: `Bearer ${
              JSON.parse(sessionStorage.getItem("user"))?.accessToken
            }`,
          },
        }
      );
      setData(response?.data?.data || []);
    } catch (error) {
      ErrorToast("Failed to load customer feedback data");
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBranches();
    fetchCustomerFeedbackData();
  }, []);


  const filteredData = useMemo(() => {
    let filtered = [...data];
    if (searchFilters.branch) {
      filtered = filtered.filter(
        (item) =>
          item.branchName?.toLowerCase() === searchFilters.branch.toLowerCase()
      );
    }
    if (searchFilters.chequeHandoverMode) {
        filtered = filtered.filter(item => item.chequeHandoverMode === searchFilters.chequeHandoverMode);
    }
    if (searchFilters.dateRange && searchFilters.dateRange.length === 2) {
      const [startDate, endDate] = searchFilters.dateRange;
      filtered = filtered.filter((item) => {
        const itemDate = parseDate(item.created);
        if (!itemDate) return false;
        // Check if itemDate is within the range (inclusive)
        return itemDate >= startDate.toDate() && itemDate <= endDate.toDate();
      });
    }
    return filtered;
  }, [data, searchFilters]);

  const handleFilterChange = useCallback((key, value) => {
    setSearchFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const clearFilters = useCallback(() => {
    setSearchFilters({
      branch: "",
      chequeHandoverMode: null,
      dateRange: null,
    });
  }, []);
  
  const handleFetchAll = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${Api_Url}api/v1/CustomerFeedback/superadmin/all-cases`,
        {
          headers: {
            Authorization: `Bearer ${
              JSON.parse(sessionStorage.getItem("user"))?.accessToken
            }`,
          },
        }
      );
      setData(response?.data?.data || []);
      SuccessToast("All records loaded successfully");
    } catch (error) {
      ErrorToast("Failed to load all records");
    } finally {
      setLoading(false);
    }
  };

  const handleExportToCsv = async () => {
    try {
      const response = await axios.get(
        `${Api_Url}api/v1/CustomerFeedback/superadmin/export-all-cases`,
        {
          responseType: "blob",
          headers: {
            Authorization: `Bearer ${
              JSON.parse(sessionStorage.getItem("user"))?.accessToken
            }`,
          },
        }
      );
      const file = new Blob([response.data], { type: "text/csv" });
      const fileURL = URL.createObjectURL(file);
      const link = document.createElement("a");
      link.href = fileURL;
      link.setAttribute(
        "download",
        `CustomerFeedback_Report_${formatDate(new Date(), "YYYYMMDD")}.csv`
      );
      document.body.appendChild(link);
      link.click();
      URL.revokeObjectURL(fileURL);
      document.body.removeChild(link);
      SuccessToast("Export successful!");
    } catch (error) {
      ErrorToast("Failed to export file");
    }
  };
  
  const getRatingTag = (rating) => {
      let color = 'default';
      if (rating === 'Excellent') color = 'success';
      else if (rating === 'Good') color = 'processing';
      else if (rating === 'Average') color = 'warning';
      else if (rating === 'Poor') color = 'error';
      return <Tag color={color}>{rating || 'N/A'}</Tag>;
  };

  const columns = [
    {
        title: "Branch",
        dataIndex: "branchName",
        sorter: (a, b) => a.branchName?.localeCompare(b.branchName),
    },
    {
        title: "Application ID",
        dataIndex: "applicationId",
        width: 120,
    },
    {
        title: "Customer Name",
        dataIndex: "customerName",
    },
    {
        title: "Handover Date",
        dataIndex: "chequeHandoverDate",
        render: (date) => formatDate(date, "DD-MM-YYYY"),
        sorter: (a, b) => parseDate(a.chequeHandoverDate) - parseDate(b.chequeHandoverDate),
        width: 120,
    },
    {
        title: "Handover Mode",
        dataIndex: "chequeHandoverMode",
        render: (mode) => <Tooltip title={mode}>{mode?.substring(0, 15)}...</Tooltip>,
    },
    {
        title: "Overall Rating",
        dataIndex: "overallExperienceRating",
        render: getRatingTag,
        width: 100,
    },
    {
        title: "Avg Time",
        dataIndex: "averageTimeInHrsMin",
        width: 100,
    },
    {
        title: "Attending Emp",
        dataIndex: "attendingEmployeeCode",
        render: (_, record) => <Tooltip title={record.attendingEmployeeName}>{record.attendingEmployeeCode || 'N/A'}</Tooltip>,
        width: 100,
    },
    {
        title: "Digital Consent",
        dataIndex: "digitalConsentStatus",
        render: (status) => (
            <Tag color={status === "Customer OTP Validated" ? "green" : "volcano"}>
                {status}
            </Tag>
        ),
        width: 150,
    },
    {
        title: "Submitted On",
        dataIndex: "created",
        render: (date) => formatDate(date, "DD-MM-YYYY hh:mm A"),
        sorter: (a, b) => parseDate(a.created) - parseDate(b.created),
        width: 140,
    },
    {
        title: "Submitted By (User)",
        dataIndex: "createdBy",
        width: 120,
    },
    {
        title: "Submitted By (Role)",
        dataIndex: "submittedByRole",
        width: 100,
    },
  ];

  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <div className="row">
          <div className="col-12 d-flex align-items-center justify-content-between">
            <h3 className="mb-0">Customer Feedback</h3>
            <div className="d-flex align-items-center gap-2">
              {sessionStorage.getItem("user") &&
                ["AdminFacilities", "SuperAdmin"].some((role) =>
                  JSON.parse(sessionStorage.getItem("user")).roles?.includes(role)
                ) && (
                  <>
                    <button onClick={handleFetchAll} className="btn btn-info" disabled={loading}>
                      {loading ? "Loading..." : "Show All Cases"}
                    </button>
                    <button onClick={handleExportToCsv} className="btn btn-success">
                      <i className="fa-solid fa-file-excel me-2"></i>Export
                    </button>
                  </>
                )}
              <button className="btn btn-primary" onClick={handleOpenModal}>
                Add Customer Feedback
              </button>
            </div>
          </div>
        </div>

        <Card className="my-3">
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={6}>
              <Select
                placeholder="Filter by Branch"
                value={searchFilters.branch || undefined}
                onChange={(value) => handleFilterChange("branch", value)}
                allowClear
                style={{ width: "100%" }}
                showSearch
              >
                {branches.map((branch) => (
                  <Option key={branch} value={branch}>
                    {branch}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Select
                placeholder="Filter by Handover Mode"
                value={searchFilters.chequeHandoverMode}
                onChange={(value) => handleFilterChange("chequeHandoverMode", value)}
                allowClear
                style={{ width: "100%" }}
              >
                <Option value={null}>All Modes</Option>
                {HANDOVER_MODES.map((mode) => (
                    <Option key={mode} value={mode}>{mode}</Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <RangePicker
                value={searchFilters.dateRange}
                onChange={(dates) => handleFilterChange("dateRange", dates)}
                style={{ width: "100%" }}
                format="DD/MM/YYYY"
              />
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Button onClick={clearFilters} type="default">
                Clear Filters
              </Button>
            </Col>
          </Row>
        </Card>

        <div className="row">
          <div className="col-md-12">
            <div className="table-responsive">
              <Table
                className="table-striped"
                columns={columns}
                dataSource={filteredData}
                rowKey={(record) => record.id}
                loading={loading}
                pagination={{ showSizeChanger: true }}
                scroll={{ x: 1300 }} 
              />
            </div>
          </div>
        </div>

        {isModalOpen && (
          <CustomerFeedbackModalAdd
            onClose={handleCloseModal}
            onSuccess={fetchCustomerFeedbackData}
          />
        )}
      </div>
    </div>
  );
};